import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface RefundPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function RefundPolicyModal({
  isOpen,
  onClose
}: RefundPolicyModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-157">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-id="element-158">
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} transition={{
        duration: 0.25
      }} className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-159">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-gray-200 bg-cream" data-id="element-160">
              <h2 className="text-xl md:text-2xl font-bold text-burgundy" data-id="element-161">
                Política de Reembolsos y Devoluciones
              </h2>
              <button onClick={onClose} aria-label="Cerrar" className="p-2 rounded-full hover:bg-burgundy/10 text-dark transition-colors" data-id="element-162">
                <X className="w-5 h-5" data-id="element-163" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 md:px-8 py-6 text-gray-800" style={{
          fontSize: '15px',
          lineHeight: 1.7
        }} data-id="element-164">
              <p className="font-bold text-burgundy mb-4" data-id="element-165">
                Kit 5x5 Ciclo en Calma
              </p>

              <p className="mb-4" data-id="element-166">
                Debido a la naturaleza digital de nuestros productos, todas las
                ventas del Kit 5x5 Ciclo en Calma, sus guías, bonus,
                meditaciones, trackers, plantillas, IA personalizada y cualquier
                otro material incluido son <strong data-id="element-167">finales</strong>.
              </p>

              <p className="mb-4" data-id="element-168">
                Una vez completada la compra, recibirás acceso al contenido
                digital adquirido, por lo que no podemos ofrecer devoluciones,
                cambios o cancelaciones por arrepentimiento de compra, falta de
                uso del producto, error al comprar, interpretación personal del
                contenido o expectativas no alineadas con la descripción del
                producto.
              </p>

              <p className="mb-4" data-id="element-169">
                Te recomendamos revisar cuidadosamente todos los detalles del
                producto antes de realizar la compra, para asegurarte de que se
                ajusta a tus necesidades y expectativas.
              </p>

              <p className="mb-4" data-id="element-170">
                El Kit 5x5 Ciclo en Calma es un producto educativo de hábitos,
                autocuidado, organización del ciclo, alimentación simple,
                registro personal y regulación emocional.{' '}
                <strong data-id="element-171">
                  No reemplaza atención médica, ginecológica, psicológica,
                  nutricional ni ningún tratamiento profesional.
                </strong>
              </p>

              <p className="mb-4" data-id="element-172">
                Si tenés algún problema para acceder, descargar o visualizar tu
                guía, bonus, meditación, tracker, IA personalizada o cualquier
                material incluido, podés contactarnos a:
              </p>
              <p className="mb-6" data-id="element-173">
                <a href="mailto:adonisclubstore@gmail.com" className="text-burgundy font-bold underline" data-id="element-174">
                  adonisclubstore@gmail.com
                </a>
              </p>

              <p className="mb-6" data-id="element-175">
                Nuestro equipo te ayudará para que puedas recibir correctamente
                todo el material adquirido.
              </p>

              <h3 className="text-lg font-bold text-burgundy mt-8 mb-3" data-id="element-176">
                Condiciones de Garantía de Reembolso
              </h3>
              <p className="mb-3" data-id="element-177">
                En Kit 5x5 Ciclo en Calma, confiamos en la calidad del
                contenido, herramientas y recursos que ofrecemos.
              </p>
              <p className="mb-3" data-id="element-178">
                Por eso, en caso de que el producto incluya una{' '}
                <strong data-id="element-179">Garantía de Devolución de 30 días</strong>, esta estará
                sujeta a las siguientes condiciones.
              </p>
              <p className="mb-4" data-id="element-180">
                Al comprar, aceptás los siguientes términos:
              </p>

              <h4 className="font-bold text-dark mt-6 mb-2" data-id="element-181">Elegibilidad</h4>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-182">
                <li data-id="element-183">
                  Los reembolsos solo estarán disponibles dentro de los{' '}
                  <strong data-id="element-184">30 días corridos</strong> posteriores a la fecha de
                  compra.
                </li>
                <li data-id="element-185">
                  Para solicitar un reembolso, deberás demostrar que realmente
                  consumiste, trabajaste y aplicaste el material del producto.
                </li>
              </ul>

              <p className="mb-2 font-medium" data-id="element-186">Esto puede incluir:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-187">
                <li data-id="element-188">
                  Capturas, notas o registros completados de la Guía 5x5 Ciclo
                  en Calma.
                </li>
                <li data-id="element-189">
                  Evidencia de haber usado el Tracker 5x5 para registrar
                  síntomas, dolor, hinchazón, humor, energía, sueño o hábitos.
                </li>
                <li data-id="element-190">
                  Prueba de haber aplicado las recomendaciones prácticas del kit
                  durante tus días previos o durante tu periodo.
                </li>
                <li data-id="element-191">
                  Evidencia de haber revisado o utilizado los bonus incluidos,
                  como recetas, meditación o guía de qué hacer durante el
                  periodo.
                </li>
                <li data-id="element-192">
                  Capturas o ejemplos de consultas realizadas a la IA Ciclo en
                  Calma, en caso de haberla utilizado.
                </li>
                <li data-id="element-193">
                  Explicación clara de por qué considerás que el producto no
                  cumplió con lo prometido, luego de haberlo aplicado de forma
                  activa.
                </li>
              </ul>

              <p className="mb-4" data-id="element-194">
                Las solicitudes realizadas antes de los <strong data-id="element-195">14 días</strong>{' '}
                desde la compra no serán aceptadas, ya que consideramos
                necesario un tiempo mínimo para consumir, aplicar e integrar el
                material.
              </p>

              <h4 className="font-bold text-dark mt-6 mb-2" data-id="element-196">Exclusiones</h4>
              <p className="mb-2" data-id="element-197">
                No se otorgarán reembolsos en los siguientes casos:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-198">
                <li data-id="element-199">
                  Compras de productos digitales marcados expresamente como
                  venta final.
                </li>
                <li data-id="element-200">Solicitudes por arrepentimiento de compra.</li>
                <li data-id="element-201">Solicitudes por falta de uso del producto.</li>
                <li data-id="element-202">
                  Solicitudes por no haber leído correctamente la descripción
                  del producto antes de comprar.
                </li>
                <li data-id="element-203">
                  Clientas que ya hayan solicitado previamente un reembolso por
                  el mismo producto.
                </li>
                <li data-id="element-204">
                  Casos donde no se presente prueba suficiente de haber
                  trabajado y aplicado el material.
                </li>
                <li data-id="element-205">
                  Solicitudes realizadas fuera del plazo de 30 días corridos
                  desde la compra.
                </li>
                <li data-id="element-206">
                  Solicitudes basadas en expectativas médicas, diagnósticas o
                  terapéuticas, ya que el producto es educativo y no reemplaza
                  atención profesional.
                </li>
                <li data-id="element-207">
                  Casos donde el dolor, síntomas o malestar requieran evaluación
                  médica profesional.
                </li>
              </ul>

              <p className="mb-4" data-id="element-208">
                Debido a la naturaleza digital del producto, una vez aprobado el
                reembolso, el acceso a la guía, bonus, tracker, meditación, IA
                personalizada y cualquier otro contenido relacionado será
                revocado.
              </p>

              <h4 className="font-bold text-dark mt-6 mb-2" data-id="element-209">
                Proceso para Solicitar un Reembolso
              </h4>
              <p className="mb-3" data-id="element-210">
                Para solicitar un reembolso, deberás contactar a nuestro equipo
                de soporte dentro del plazo correspondiente escribiendo a:
              </p>
              <p className="mb-4" data-id="element-211">
                <a href="mailto:adonisclubstore@gmail.com" className="text-burgundy font-bold underline" data-id="element-212">
                  adonisclubstore@gmail.com
                </a>
              </p>

              <p className="mb-2 font-medium" data-id="element-213">Tu solicitud deberá incluir:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-214">
                <li data-id="element-215">Nombre completo.</li>
                <li data-id="element-216">Email utilizado en la compra.</li>
                <li data-id="element-217">Fecha de compra.</li>
                <li data-id="element-218">Motivo de la solicitud.</li>
                <li data-id="element-219">
                  Prueba de haber completado, trabajado y aplicado el material,
                  según lo explicado anteriormente.
                </li>
                <li data-id="element-220">
                  En caso de corresponder, capturas del tracker, notas,
                  ejercicios, consultas realizadas a la IA o cualquier evidencia
                  de uso activo del kit.
                </li>
              </ul>

              <p className="mb-4" data-id="element-221">
                Una vez recibida la solicitud, nuestro equipo revisará el caso y
                responderá dentro de un plazo razonable.
              </p>

              <p className="mb-6" data-id="element-222">
                Si el reembolso es aprobado, el dinero será devuelto al método
                de pago original dentro de un plazo estimado de{' '}
                <strong data-id="element-223">5 a 10 días hábiles</strong>, dependiendo de la entidad
                bancaria o plataforma de pago utilizada.
              </p>

              <h4 className="font-bold text-dark mt-6 mb-2" data-id="element-224">
                Nuestro Compromiso
              </h4>
              <p className="mb-3" data-id="element-225">
                Esta política está diseñada para proteger tanto a nuestras
                clientas como la integridad de nuestro contenido digital.
              </p>
              <p className="mb-3" data-id="element-226">
                Queremos que tengas una experiencia clara, segura y positiva con
                el Kit 5x5 Ciclo en Calma.
              </p>
              <p className="mb-3" data-id="element-227">
                Si realizaste el trabajo, aplicaste el material, usaste las
                herramientas incluidas y aun así considerás que el producto no
                cumplió con lo prometido, vamos a revisar tu caso con seriedad y
                compromiso.
              </p>
              <p className="mb-4" data-id="element-228">
                Nuestro objetivo es que tengas claridad, herramientas prácticas
                y acompañamiento para entender mejor tu ciclo y dejar de
                improvisar todos los meses.
              </p>

              <p className="bg-cream border-l-4 border-burgundy p-4 text-sm text-gray-700 italic" data-id="element-229">
                El Kit 5x5 Ciclo en Calma no promete curas, diagnósticos ni
                resultados médicos garantizados. Es una herramienta educativa de
                autocuidado para ayudarte a prepararte, registrar tu patrón y
                acompañar tu cuerpo con más calma antes y durante tu periodo.
              </p>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 md:px-8 py-4 bg-cream flex justify-end" data-id="element-230">
              <button onClick={onClose} className="bg-burgundy hover:bg-burgundy-dark text-white font-bold px-6 py-2.5 rounded-lg transition-colors" data-id="element-231">
                Cerrar
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}